import { useMemo } from "react";
import { useDebouncedSearch } from "@/lib/hooks/search/use-debounced-search";
import { useContacts, useClientSearch } from "@/lib/hooks/use-api";

/**
 * Hook to manage client select data (search, options, loading state).
 * Only used when a client-select field exists in the form.
 */
export function useClientSelectData(hasClientSelectField: boolean) {
  // Search state for client selection
  const { searchValue, debouncedSearchValue, setSearchValue } = useDebouncedSearch({
    initialValue: "",
    delay: 300,
  });

  // Client search hooks - only call if needed. `useContacts` defaults to
  // `staleTime: 0` + `refetchOnMount: true`, so an unguarded call fires a real
  // Supabase round-trip every single time this form mounts — and it mounts
  // fresh on every open, since the modal it lives in unmounts its content on
  // close (see ResponsiveModal → Mantine's `Modal`/`Transition`, which has no
  // `keepMounted`). For any entity whose form has no client-select field
  // (`hasClientSelectField === false`), that fetch's result is thrown away by
  // the `clientOptions` memo below — so it was pure wasted latency on every
  // create-modal open, not just Task's. Gate it so only forms that actually
  // render a client-select field pay this cost.
  const { data: initialClients, isLoading: initialClientsLoading } = useContacts(
    { isActive: true, limit: 20 },
    { enabled: hasClientSelectField }
  );
  const { data: searchResults, isFetching: isSearching } = useClientSearch(
    hasClientSelectField ? debouncedSearchValue : ""
  );

  const clientOptions = useMemo(() => {
    if (!hasClientSelectField) return [];
    return debouncedSearchValue ? searchResults : initialClients;
  }, [debouncedSearchValue, searchResults, initialClients, hasClientSelectField]);

  const clientsLoading = initialClientsLoading || isSearching;

  return {
    clientOptions,
    clientsLoading,
    searchValue,
    setSearchValue,
    isSearching,
  };
}
